import { PropertiesPanel } from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LIGHT_KIND, LIGHT_KIND_OPTIONS, LightConfigHelpers, LightNode, lightIcon, restingDirection, useLightPropertiesCreator } from '../lightsEditorConfig';
import InspectorShell from '../inspectorShell';
import { T } from 'src/shared/helpers';

// L'involucro sta in `InspectorShell`, uno per tutti i generi: qui resta ciò che è solo di
// una luce.

// Utility per normalizzare colori hex
const normalizeHexColor = (color: string): string => {
    if (!color) return '#FFFFFF';

    // Se non inizia con #, aggiungilo
    if (!color.startsWith('#')) {
        color = '#' + color;
    }

    // Se non è valido, ritorna bianco
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
        console.warn('Invalid hex color:', color, 'using white as fallback');
        return '#FFFFFF';
    }

    // Assicurati che sia maiuscolo
    return color.toUpperCase();
};

// Interfaccia semplificata - riceve solo l'oggetto light completo
export interface LightOverlayProps {
    light: {
        id: string;
        name: string;
        lightType: string;
        active?: boolean;
        intensity: number;
        diffuseColor: string;

        groundColor?: string;
        position?: [number, number, number];
        direction?: [number, number, number];
        angle?: number;
        falloff?: number;
        intensityMax?: number;
        /** Dove «Reset transform» la rimette: sopra il modello, per puntiformi e faretti. */
        restingPose?: { position: [number, number, number]; direction: [number, number, number] };
    };
    onUpdate: (updates: Partial<LightOverlayProps['light']>) => void;
    /** Cambia la specie della luce: dietro, il motore la rifà. */
    onChangeType: (lightType: string) => void;
    /** «Dove sta?» — la freccetta della testata: accende la riga di questa luce nell'elenco. */
    onReveal?: () => void;
}

// Due terne uguali sono la stessa terna: `[0,1,0]` letto due volte sono due array diversi, e un
// confronto per identità direbbe «cambiato» a ogni respiro.
const same = (a: any, b: any) => (Array.isArray(a) && Array.isArray(b) ? a.length === b.length && a.every((value, index) => value === b[index]) : a === b);

const LightsOverlayComponent: FC<LightOverlayProps> = ({ light, onUpdate, onChangeType, onReveal }) => {
    // Hook per creare le proprietà delle luci
    const createLightProperties = useLightPropertiesCreator();

    // Stato interno per le proprietà della luce - pattern MaterialOverlayComponent/ObjectOverlayComponent
    const [localLight, setLocalLight] = useState<LightNode>(light as LightNode);

    // LO STATO INTERNO SERVE A NON PERDERE QUELLO CHE SI STA SCRIVENDO: un campo riscritto da
    // fuori mentre ci si digita dentro è inservibile. Ma si risincronizzava SOLO cambiando luce, e
    // così tutto ciò che cambiava da fuori restava fuori — spegnere una luce dall'occhio della sua
    // riga lasciava «Active» acceso qui: due segni per lo stesso stato, che raccontavano due storie.
    //
    // Il confronto giusto è con l'ULTIMA LUCE RICEVUTA, non con quella che si vede a schermo: si
    // adotta ciò che è cambiato davvero là fuori, e un campo che da fuori non ha toccato nessuno
    // resta com'è, con dentro quello che si sta scrivendo.
    const lastLightRef = useRef(light);

    useEffect(() => {
        const previous = lastLightRef.current;
        lastLightRef.current = light;

        if (previous.id !== light.id) {
            setLocalLight(light as LightNode);
            return;
        }

        const changed = (Object.keys(light) as (keyof LightNode)[]).filter((key) => !same(light[key], (previous as any)[key]));
        if (!changed.length) return;

        setLocalLight((prev) => {
            const next: any = { ...prev };
            changed.forEach((key) => (next[key] = (light as any)[key]));
            return next;
        });
    }, [light]);

    const currentValues = useMemo(
        () => ({
            ...localLight,
            name: localLight.name || 'Unnamed Light',
            intensity: typeof localLight.intensity === 'number' ? localLight.intensity : 1,
            diffuseColor: normalizeHexColor(localLight.diffuseColor),

            groundColor: normalizeHexColor(localLight.groundColor || '#000000'),
            position: localLight.position || ([0, 0, 0] as [number, number, number]),
            direction: localLight.direction || ([0, -1, 0] as [number, number, number]),
            angle: typeof localLight.angle === 'number' ? localLight.angle : 30,
            falloff: typeof localLight.falloff === 'number' ? localLight.falloff : 100
        }),
        [localLight]
    );

    // Helper per aggiornare sia lo stato locale che quello esterno - pattern ObjectOverlayComponent
    const updateProperty = useCallback(
        <K extends keyof LightNode>(key: K, value: LightNode[K]) => {
            // Aggiorna lo stato locale immediatamente per UI responsiva
            setLocalLight((prev) => ({
                ...prev,
                [key]: value
            }));

            // Valida i colori prima di inviarli
            if (key === 'diffuseColor' || key === 'groundColor') {
                const normalizedColor = normalizeHexColor(value as string);
                onUpdate({ [key]: normalizedColor } as any);
            } else {
                onUpdate({ [key]: value } as any);
            }
        },
        [onUpdate]
    );

    // Funzione per aggiornare vettori (posizione/direzione) - pattern ObjectOverlayComponent
    const updateVector = useCallback(
        (vectorType: 'position' | 'direction', axis: 'x' | 'y' | 'z', value: number) => {
            const currentVector = currentValues[vectorType];
            const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
            const newVector = [...currentVector] as [number, number, number];
            newVector[axisIndex] = value;

            updateProperty(vectorType, newVector);
        },
        [currentValues, updateProperty]
    );

    // Handler per il cambio di colore - gestisce l'interfaccia ColorSelection
    const handleColorChange = useCallback(
        (key: 'diffuseColor' | 'groundColor') => {
            return (_: any, color: string) => {
                updateProperty(key, color);
            };
        },
        [updateProperty]
    );

    // AZZERARE È UNA COSA SOLA su due terne: al centro della scena, e rivolta dove il suo verso
    // «normale» è (`restingDirection`).
    //
    // Passa da qui e non da due `updateProperty` in fila perché deve arrivare come UN aggiornamento:
    // due di seguito sono due ridisegni e due passaggi nel motore per un gesto solo.
    //
    // PUNTIFORMI E FARETTI TORNANO SOPRA IL MODELLO (Elena, 2026-10-14), dove nasce una luce nuova
    // (`restingPose`): l'origine, su un modello grande, è dentro il modello. Intensità e cono restano.
    const restingPose = light.restingPose;
    const resetTransform = useCallback(() => {
        const reset = restingPose ?? {
            position: [0, 0, 0] as [number, number, number],
            direction: restingDirection(localLight.lightType)
        };

        setLocalLight((prev) => ({ ...prev, ...reset }));
        onUpdate(reset);
    }, [localLight.lightType, onUpdate, restingPose]);

    // Crea gli helpers per la configurazione delle proprietà
    const helpers: LightConfigHelpers = useMemo(
        () => ({
            updateProperty,
            updateVector,
            handleColorChange,
            resetTransform
        }),
        [updateProperty, updateVector, handleColorChange, resetTransform]
    );

    // Genera le proprietà usando il factory pattern
    const properties = useMemo(() => {
        return createLightProperties(currentValues, helpers);
    }, [createLightProperties, currentValues, helpers]);

    return (
        <InspectorShell
            kind='light'
            name={currentValues.name}
            // L'ICONA È QUELLA DELLA SPECIE, e una luce spenta lo dice accanto al nome (prototipo, chip Off).
            icon={lightIcon(currentValues.lightType)}
            state={currentValues.active === false ? [T._('Off', 'Admin')] : undefined}
            kindChoice={{
                label: LIGHT_KIND[currentValues.lightType] ?? currentValues.lightType,
                entries: LIGHT_KIND_OPTIONS,
                current: currentValues.lightType,
                onPick: (value) => {
                    if (value !== currentValues.lightType) onChangeType(String(value));
                }
            }}
            onRename={(name) => updateProperty('name', name)}
            onReveal={onReveal}
        >
            <PropertiesPanel properties={properties} />
        </InspectorShell>
    );
};

export default LightsOverlayComponent;
